import { FieldbookMap } from "@/components/common/FieldbookMap"
import { useLocalGeospatialData } from "@/hooks/useLocalGeospatialData"
import { useGeospatialDisplayPrefs } from "@/hooks/useGeospatialDisplayPrefs"
import { useGeospatialUpload } from "@/hooks/useGeospatialUpload"
import { Badge } from "@fieldbook/ui/badge"
import { Button } from "@fieldbook/ui/button"
import { Switch } from "@fieldbook/ui/switch"
import { Label } from "@fieldbook/ui/label"
import { Spinner } from "@fieldbook/ui/spinner"
import { ColorPicker } from "@fieldbook/ui/color-picker"
import { DEFAULT_GEOSPATIAL_COLOR } from "@fieldbook/common/utils"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Pencil, Trash, Upload } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
} from "@fieldbook/ui/alert-dialog"
import { useOpenClose } from "@fieldbook/ui/hooks"
import { useProjectData } from "@/contexts/ProjectData"
import { GeospatialRecordMapSource } from "@/components/geospatial/RecordSource"
import { SyncStatusBadge } from "@/components/common/SyncStatusBadge"
import { PhotoGrid } from "@/components/common/PhotoGrid"
import { usePhotosForEntity } from "@/hooks/usePhotosForEntity"
import { useAuth } from "@/hooks/useAuth"
import { Suspense } from "react"
import { canEditRecord } from "../../-geospatialForm"

export const Route = createFileRoute(
  "/_private/projects/$id/data/geospatial/$recordId",
)({
  component: RouteComponent,
})

function RouteComponent() {
  const nav = Route.useNavigate()
  return (
    <div>
      <div className="flex flex-col">
        <button
          onClick={() => nav({ to: "/projects/$id/data/geospatial" })}
          className="text-muted-foreground mb-4 mt-2 flex items-center gap-1 text-sm focus-visible:outline-0"
        >
          <ArrowLeft size={12} />
          <span>Back</span>
        </button>

        <GeospatialDetail />
      </div>
    </div>
  )
}

function formatPropertyValue(value: unknown): string {
  if (value === null || value === undefined) return "—"
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

function GeospatialDetail() {
  const { id: projectId, recordId } = Route.useParams()
  const navigate = useNavigate()
  const { getRecord, deleteRecord, toggleDisplayOnMap } =
    useLocalGeospatialData(projectId)
  const { getPref, setColorOverride, clearColorOverride, setShowLabel } =
    useGeospatialDisplayPrefs()
  const { mutateAsync: upload, isPending: isUploading } = useGeospatialUpload()
  const {
    data: { people },
  } = useProjectData()
  const { user, role } = useAuth()
  const { open, isOpen, close } = useOpenClose()
  const { data: photos } = usePhotosForEntity({
    entityId: recordId,
    entityType: "geospatial_data",
  })

  const record = getRecord(recordId)

  if (!record) {
    return <div className="p-4">Record not found</div>
  }

  const pref = getPref(recordId)

  const geoJsonFeature: GeoJSON.Feature = {
    type: "Feature",
    geometry: record.geojson,
    properties: { name: record.name, id: record.id },
  }

  const handleDelete = async () => {
    await deleteRecord(recordId)
    close()
    navigate({
      to: "/projects/$id/data/geospatial",
      params: { id: projectId },
    })
  }

  const handleUpload = async () => {
    await upload(recordId)
  }

  const propertyEntries = record.properties
    ? Object.entries(record.properties).filter(
        ([, v]) => v !== null && v !== undefined && String(v).length > 0,
      )
    : []

  const creator = people?.find((p) => p.id === record.created_by)
  const canEdit = canEditRecord({
    currentUserId: user?.id,
    creatorId: record.created_by,
    role,
  })

  return (
    <div>
      <div className="mb-2 flex flex-col gap-2">
        <div className="flex w-full justify-between">
          <span className="text-xl">{record.name}</span>
          <Badge variant="outline">{record.geometry_type}</Badge>
        </div>

        {record.notes && (
          <p className="text-muted-foreground text-sm">{record.notes}</p>
        )}

        <div className="flex w-full justify-between">
          {record.created_at && (
            <div className="text-muted-foreground text-sm">
              {new Date(record.created_at).toLocaleString()}
            </div>
          )}
          {creator && (
            <div className="text-muted-foreground text-sm">
              Created by {creator.name ?? "Unknown"}
            </div>
          )}
        </div>

        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            {record.uploaded && <SyncStatusBadge entityId={recordId} />}
          </div>
          <div className="flex items-center gap-1">
            <Label>
              Display on Map
              <Switch
                checked={record.displayOnMap}
                onCheckedChange={() => toggleDisplayOnMap(recordId)}
              />
            </Label>
            {!record.uploaded && (
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                variant="outline"
                size="sm"
              >
                {isUploading ? (
                  <Spinner />
                ) : (
                  <>
                    <Upload size={16} className="mr-1" />
                    Upload
                  </>
                )}
              </Button>
            )}
            {canEdit && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/projects/$id/geospatial/$recordId/edit",
                    params: { id: projectId, recordId },
                  })
                }
                variant="outline"
                aria-label="Edit record"
              >
                <Pencil size={16} />
              </Button>
            )}
            <Button onClick={() => open()} variant="outline">
              <Trash size={16} />
            </Button>
          </div>
        </div>

        <div className="border-muted flex flex-col gap-2 border-t pt-2">
          {/* Device-only: the shared colour is set when the record is
              created (here or on the web), so this never writes to the
              server. */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-sm">
              Display Colour
            </span>
            <div className="flex items-center gap-2">
              {pref.colorOverride && (
                <Button
                  onClick={() => clearColorOverride(recordId)}
                  variant="outline"
                  size="sm"
                >
                  Reset
                </Button>
              )}
              <ColorPicker
                value={pref.colorOverride}
                fallbackColor={record.color ?? DEFAULT_GEOSPATIAL_COLOR}
                label={`Colour for ${record.name} on this device`}
                onChange={(color) => setColorOverride(recordId, color)}
              />
            </div>
          </div>

          <Label className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">Show label</span>
            <Switch
              checked={pref.showLabel}
              onCheckedChange={(checked) => setShowLabel(recordId, checked)}
            />
          </Label>
        </div>
      </div>
      {photos.length > 0 && (
        <div className="mb-2 flex flex-col gap-2">
          <span className="text-muted-foreground text-sm">Photos</span>
          <Suspense
            fallback={
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p) => (
                  <div
                    key={p.id}
                    className="bg-muted aspect-square animate-pulse rounded"
                  />
                ))}
              </div>
            }
          >
            <PhotoGrid photoIds={photos.map((p) => p.id)} />
          </Suspense>
        </div>
      )}

      <div className="mb-2">
        {propertyEntries.length > 0 && (
          <div className="border-muted mt-1 space-y-0.5 border-y py-1 text-xs">
            <span className="text-foreground text-sm">Properties</span>
            {propertyEntries.map(([key, value]) => (
              <div key={key}>
                <span className="text-muted-foreground">{key}:</span>{" "}
                <span className="wrap-break-word font-medium">
                  {formatPropertyValue(value)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <FieldbookMap geoJSON={geoJsonFeature}>
        <GeospatialRecordMapSource recordId={recordId} projectId={projectId} />
      </FieldbookMap>

      {isOpen && (
        <AlertDialog open={isOpen}>
          <AlertDialogHeader>Delete Record</AlertDialogHeader>
          <AlertDialogContent>
            <p>
              Are you sure you want to delete "{record.name}"? This will delete
              the item for all users. This action cannot be undone.
            </p>
            <AlertDialogCancel
              className="border-muted-foreground text-foreground border"
              onClick={close}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-50"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}
